import {
  Typography,
  Checkbox,
  IconButton,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import React from 'react';
import ProductDocumentsToDistributeDialogDocumentsSection from './ProductDocumentsToDistributeDialogDocumentsSection';
import { documentStyle, openedDocumentStyle } from './dialogStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClearIcon from '@mui/icons-material/Clear';
import { DistributeItem } from './ProductDocumentsToDistributeDialog';

type LanguageMap = {
  languagesPerGroupedAgreement: { serviceName: string; value: string }[];
  languagesPerAgreement: { agreementId: string; value: string }[];
  languagesPerDocument: { documentUid: string; value: string }[];
};

const resolveKeyForItemWithoutServiceAgreement = (item: DistributeItem): string =>
  item.serviceAgreementId ?? item.taskId;

type ProductDocumentsToDistributeDialogAgreementSectionProps = {
  setLanguangeMap: React.Dispatch<React.SetStateAction<LanguageMap>>;
  serviceAgreements: DistributeItem[];
  toggleItem: (e: React.MouseEvent<HTMLButtonElement>, key: string) => void;
  openItems: Record<string, boolean>;
  checkedAgreements: string[];
  handleSelectItem: (item: DistributeItem, serviceAgreements?: DistributeItem[]) => void;
  checkedDocuments: string[];
  handleSelectDocument: (doc: { documentUid: string; [key: string]: unknown }) => void;
};

const ProductDocumentsToDistributeDialogAgreementSection = ({
  setLanguangeMap,
  serviceAgreements,
  toggleItem,
  openItems,
  checkedAgreements,
  handleSelectItem,
  checkedDocuments,
  handleSelectDocument,
}: ProductDocumentsToDistributeDialogAgreementSectionProps) => {
  const [selectedLanguageForAgreementDocuments, setSelectedLanguageForAgreementDocuments] = React.useState<
    { key: string; value: string }[]
  >([]);

  const handleChange = (e: { target: { value: string } } | null, key: string) => {
    if (!e) {
      setSelectedLanguageForAgreementDocuments((prev) => prev.filter((a) => a.key !== key));
      setLanguangeMap((prev) => ({
        ...prev,
        languagesPerAgreement: prev?.languagesPerAgreement.filter((a) => a.agreementId !== key),
      }));
      return;
    }

    setSelectedLanguageForAgreementDocuments((prev) => [
      ...prev.filter((a) => a.key !== key),
      { key: key, value: e.target.value },
    ]);
    setLanguangeMap((prev) => ({
      ...prev,
      languagesPerAgreement: [
        ...(prev?.languagesPerAgreement?.filter((a) => a.agreementId !== key) ?? []),
        { agreementId: key, value: e.target.value },
      ],
    }));
  };

  return serviceAgreements?.map((item: DistributeItem, index: number) => {
    const itemKey = resolveKeyForItemWithoutServiceAgreement(item);

    return (
      <Grid item xs={12} sm={12} key={index} sx={documentStyle}>
        {index !== 0 && <Divider sx={{ height: '2px', width: '100%', mt: -2, mb: 2 }} />}
        <Grid container item xs={12}>
          <Typography variant="body1">Agreement in status: {item.status}</Typography>
        </Grid>
        <Grid container>
          <Grid item xs={1}>
            <Checkbox
              data-testid={`agreement-checkbox`}
              checked={checkedAgreements?.some((a: string) =>
                item.documents?.map((d: { documentUid: string }) => d.documentUid)?.includes(a),
              )}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectItem(item, serviceAgreements);
              }}
            />
          </Grid>
          <Grid item xs={7} sx={{ alignItems: 'center', display: 'flex' }}>
            <Typography variant="body1">Documents: {item?.documents?.length}</Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex' }}>
            <Grid item xs={9} sx={{ display: 'grid', width: '100%' }}>
              {!openItems[itemKey] && (
                <FormControl size="small">
                  <InputLabel
                    sx={{ mb: 1 }}
                    data-testid="demo-simple-select-filled-label"
                    id="demo-simple-select-filled-label"
                  >
                    Languange
                  </InputLabel>
                  <Select
                    endAdornment={
                      selectedLanguageForAgreementDocuments?.filter((a) => a.key === itemKey)[0] && (
                        <ClearIcon
                          sx={{ height: '0.5em', mr: 1, cursor: 'pointer' }}
                          onClick={() => handleChange(null, itemKey)}
                        />
                      )
                    }
                    label="Languange"
                    onChange={(e) => handleChange(e, itemKey)}
                    value={selectedLanguageForAgreementDocuments?.filter((a) => a.key === itemKey)[0]?.value ?? ''}
                    data-testid="demo-simple-select-filled"
                    id="demo-simple-select-filled"
                  >
                    {item.documents
                      .flatMap((doc) => doc.documentLanguages ?? [])
                      .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index)
                      .map((lang: string) => (
                        <MenuItem key={lang} value={lang}>
                          {lang}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={3} sx={{ display: 'grid', width: '100%' }}>
              <IconButton
                sx={{ ml: 1.2, p: 0 }}
                onClick={(e) => toggleItem(e, itemKey)}
                aria-label={openItems[itemKey] ? 'Collapse' : 'Expand'}
              >
                {openItems[itemKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        {openItems[itemKey] && (
          <Divider
            sx={{
              height: '2px',
              width: '100%',
              mt: 0.5,
              mb: 1,
            }}
          />
        )}
        {openItems[itemKey] && (
          <Grid container item xs={12} sx={openedDocumentStyle}>
            <ProductDocumentsToDistributeDialogDocumentsSection
              setLanguangeMap={setLanguangeMap}
              handleSelectDocument={handleSelectDocument}
              checkedDocuments={checkedDocuments}
              documents={item.documents}
            />
          </Grid>
        )}
      </Grid>
    );
  });
};

export default React.memo(ProductDocumentsToDistributeDialogAgreementSection);
